/**
 * Workshop series icon files + a static `WorkshopKey` union.
 *
 * Extracted from `client/src/pages/events.tsx` so other components (the
 * Remind Me modal, future cards) can render the same series mark without
 * pulling in the entire events page module.
 *
 * The actual `WorkshopIcon` React component lives in `./workshop-icon.tsx`
 * so this file stays type-only and tree-shakeable from non-React callers.
 */
export type WorkshopKey =
  | "claude-code"
  | "claude-code-advanced"
  | "claude-code-kids"
  | "claude-cowork"
  | "claude-design"
  | "claude-sales"
  | "ai-fundamentals"
  | "ai-video"
  | "other";

/** Base path (no extension) for each series' static icon PNG/WebP pair. */
export const WORKSHOP_ICON_FILES: Record<WorkshopKey, string | null> = {
  "claude-code":          "/images/workshop/icons/claude-code",
  "claude-code-advanced": "/images/workshop/icons/claude-code-advanced",
  "claude-code-kids":     "/images/workshop/icons/claude-code-kids",
  "claude-cowork":        "/images/workshop/icons/claude-cowork",
  "claude-design":        "/images/workshop/icons/claude-design",
  "claude-sales":         "/images/workshop/icons/claude-sales",
  "ai-fundamentals":      "/images/workshop/icons/ai-fundamentals",
  "ai-video":             null,
  "other":                null,
};

/**
 * Base path (no size, no extension) for each series' PAINTED impressionist
 * mark — the Cowork design system's subject treatment. Files are
 * `<base>-144.webp` and `<base>-288.webp`; see
 * scripts/impressionist-series-icons.sh for how they are made.
 *
 * These supersede WORKSHOP_ICON_FILES above on any surface inside .ds-cowork.
 * The flat illustrations stay for surfaces that are not on the design system.
 */
export const WORKSHOP_SERIES_MARK_FILES: Record<WorkshopKey, string | null> = {
  "claude-code":          "/images/icons/impressionist/series-code",
  "claude-code-advanced": "/images/icons/impressionist/series-code-advanced",
  "claude-code-kids":     "/images/icons/impressionist/series-kids",
  "claude-cowork":        "/images/icons/impressionist/series-cowork",
  "claude-design":        "/images/icons/impressionist/series-design",
  "claude-sales":         "/images/icons/impressionist/series-sales",
  "ai-fundamentals":      "/images/icons/impressionist/series-leader",
  /* No painted version yet; the chrome clapper stands in. */
  "ai-video":             "/images/icons/chrome-tinted/series-video",
  "other":                null,
};

/** The same seven marks in the CHROME-TINTED finish — the set the workshops
 *  index now carries on its cards. Kept beside the impressionist paths rather
 *  than derived from them so a surface picks a finish explicitly. */
export const WORKSHOP_SERIES_MARK_FILES_CHROME: Record<WorkshopKey, string | null> = {
  "claude-code":          "/images/icons/chrome-tinted/series-code",
  "claude-code-advanced": "/images/icons/chrome-tinted/series-code-advanced",
  "claude-code-kids":     "/images/icons/chrome-tinted/series-kids",
  "claude-cowork":        "/images/icons/chrome-tinted/series-cowork",
  "claude-design":        "/images/icons/chrome-tinted/series-design",
  "claude-sales":         "/images/icons/chrome-tinted/series-sales",
  "ai-fundamentals":      "/images/icons/chrome-tinted/series-leader",
  "ai-video":             "/images/icons/chrome-tinted/series-video",
  "other":                null,
};

/**
 * One dark ROOM per series — the Cowork design system's tone-on-tone pairing:
 * a dark step as the ground and a pale step FROM THE SAME FAMILY as the type
 * on it. Never a colour from another ramp; that is the thing the ramp exists
 * to prevent.
 *
 * These are the four pairings the Claude Code page already ships on its
 * persona cards (see CODE_PERSONAS in event-detail.tsx), reused verbatim, plus
 * gold and green for the two series that page has no card for. They are values
 * rather than tokens, exactly as they are there, because a room is a pairing
 * and not a single colour.
 *
 * Contrast, measured: slate 12.4:1, plum 10.3:1, lavender 9.4:1, teal 9.0:1,
 * gold 7.3:1, green 5.0:1 — all clear AA.
 */
export const WORKSHOP_ROOMS: Record<WorkshopKey, { bg: string; fg: string } | null> = {
  "claude-code":          { bg: "#1E3D3B", fg: "#CDE6E3" },  // teal 300 / 900
  "claude-code-advanced": { bg: "#1E3D3B", fg: "#CDE6E3" },  // teal — same product line
  "claude-code-kids":     { bg: "#3A4F3D", fg: "#B2C8B4" },  // green 400 / 800
  "claude-cowork":        { bg: "#16202B", fg: "#D3E1F1" },  // slate 200 / 800
  "claude-design":        { bg: "#2B3450", fg: "#DCE1F0" },  // lavender 900 / 400
  "claude-sales":         { bg: "#3B2438", fg: "#E9D8E4" },  // plum 300 / 900
  "ai-fundamentals":      { bg: "#4A3308", fg: "#E6C878" },  // gold 300 / 800
  "ai-video":             { bg: "#4A3308", fg: "#E6C878" },  // gold, the seat it took over
  "other":                { bg: "#16202B", fg: "#D3E1F1" },  // slate
};

/**
 * The same rooms in the VIVID 2026 palette (/design-system-vivid-2026/),
 * used by the workshops index only — see `.ds-viv` in cowork-ds.css.
 *
 * Kept here rather than in events.tsx deliberately: the two maps have to be
 * readable against each other, because they describe the same seven rooms in
 * two palettes and the pairing is the thing that must not drift. The site-wide
 * promo popup still reads WORKSHOP_ROOMS above, so it is untouched by this.
 *
 * Every ground is a family's DEEP step and every fg is that family's own
 * measured partner from the vivid wheel — never a colour from another family,
 * which is the one rule a room exists to keep. Contrast, measured:
 *
 *   cowork   Navy    #283750 / Cloud  #B4C8E1   7.01
 *   code     Ocean   #007891 / Paper  #F5F5F7   4.71
 *   advanced Ink     #1D1D1F / Paper  #F5F5F7  15.46
 *   kids     Canopy  #0F503C / Lime   #BACD2D   5.31
 *   design   Plum    #5E1756 / Orchid #C8AFF0   6.26
 *   sales    Crimson #9B0032 / Pink   #F3BDAF   5.21
 *   leader   Gold    #8B5F13 / Paper  #F5F5F7   5.15
 *
 * Gold takes Paper rather than Vivid Vargas on purpose: the vivid page's own
 * families table scores gold's tone-on-tone pair at 3.44 and marks it
 * `roomOk: false`, so the family cannot hold itself and the wheel's fallback
 * ink is the measured answer.
 */
export const WORKSHOP_ROOMS_VIVID: Record<WorkshopKey, { bg: string; fg: string }> = {
  "claude-code":          { bg: "#007891", fg: "#F5F5F7" },  // Ocean  / Paper
  "claude-code-advanced": { bg: "#1D1D1F", fg: "#F5F5F7" },  // Ink    / Paper
  "claude-code-kids":     { bg: "#0F503C", fg: "#BACD2D" },  // Canopy / Lime
  "claude-cowork":        { bg: "#283750", fg: "#B4C8E1" },  // Navy   / Cloud
  "claude-design":        { bg: "#5E1756", fg: "#C8AFF0" },  // Plum   / Orchid
  "claude-sales":         { bg: "#9B0032", fg: "#F3BDAF" },  // Crimson/ Pink
  "ai-fundamentals":      { bg: "#8B5F13", fg: "#F5F5F7" },  // Gold   / Paper
  "ai-video":             { bg: "#8B5F13", fg: "#F5F5F7" },  // Gold, took Leader's grid seat
  "other":                { bg: "#283750", fg: "#B4C8E1" },  // Navy
};

/** Short, human display name per series (matches workshop_series.short_name). */
export const WORKSHOP_SHORT_NAMES: Record<WorkshopKey, string | null> = {
  "claude-code":          "Claude Code",
  "claude-code-advanced": "Advanced Code",
  "claude-code-kids":     "Claude Code for Kids",
  "claude-cowork":        "Claude Cowork",
  "claude-design":        "Claude Design",
  "claude-sales":         "Claude for Sales",
  "ai-fundamentals":      "AI-First Leader",
  "ai-video":             "AI Video",
  "other":                null,
};

/**
 * Best-effort series key from any string (slug, seriesSlug, title). Mirrors
 * the matcher logic in events.tsx so a row's icon stays consistent across
 * the events page and the Remind Me modal.
 */
export function workshopKeyFromString(input: string | null | undefined): WorkshopKey {
  const s = (input ?? "").toLowerCase();
  if (!s) return "other";
  if (s.includes("design"))   return "claude-design";
  if (s.includes("cowork"))   return "claude-cowork";
  if (s.includes("sales") || s.includes("prospecting") || s.includes("pipeline"))
    return "claude-sales";
  if (s.includes("fundamentals") || s.includes("ai-fundamentals") || s.includes("ai-first-leader"))
    return "ai-fundamentals";
  if (s.includes("ai-video") || s.includes("ai video")) return "ai-video";
  if (s.includes("advanced-claude-code") || (s.includes("advanced") && s.includes("code")))
    return "claude-code-advanced";
  if (s.includes("code"))     return "claude-code";
  return "other";
}
